// webapp/src/lib/offlineImageQueue.js
//
// Offline post creation: raw image files stashed in IndexedDB while the
// device is offline, uploaded by the write-queue drain when connectivity
// returns. See docs/superpowers/specs/2026-08-09-offline-post-creation-design.md.
//
// Blobs must NEVER pass through offlineWriteQueue's toPlain (JSON round-trip
// destroys them) — this module talks to offlineStorage directly.

import { tx } from './offlineStorage';
import { uploadImageFile } from './pasteImageUpload';

const STORE = 'pendingImages';

function makeIdbStorage() {
    return {
        put: (entry) => tx('readwrite', STORE, store => store.put(entry)),
        get: (key) => tx('readonly', STORE, store => store.get(key)),
        delete: (key) => tx('readwrite', STORE, store => store.delete(key)),
    };
}

let _defaultStorage = null;
function defaultStorage() {
    if (!_defaultStorage) _defaultStorage = makeIdbStorage();
    return _defaultStorage;
}

// Test-only: in-memory storage with the same shape as makeIdbStorage.
export function _makeMemoryStorage() {
    const m = new Map();
    return {
        put: async (entry) => { m.set(entry.key, entry); },
        get: async (key) => (m.has(key) ? m.get(key) : null),
        delete: async (key) => { m.delete(key); },
        _map: m,
    };
}

let _keyCounter = 0;

export async function stashImage({ file, previewDataUrl } = {}, storage = defaultStorage()) {
    if (!file) return null;
    _keyCounter += 1;
    const key = `img-${Date.now()}-${_keyCounter}`;
    try {
        await storage.put({
            key,
            blob: file,
            name: file.name || key,
            type: file.type || '',
            size: file.size || 0,
            previewDataUrl: previewDataUrl || '',
            createdAt: Date.now(),
        });
        return key;
    } catch (e) {
        console.warn('[offlineImageQueue] stashImage failed', e);
        return null;
    }
}

// JSON-safe placeholder that renders everywhere a real descriptor would:
// composer thumbnails and feed cards read the s3*Link fields, so the local
// preview dataURL rides in all of them until the real upload replaces it.
export function makePendingDescriptor({ key, name, previewDataUrl } = {}) {
    const preview = previewDataUrl || '';
    return {
        _pendingUpload: true,
        pendingImageKey: key,
        name: name || key,
        ext: '',
        s3SmallLink: preview,
        s3MediumLink: preview,
        s3LargeLink: preview,
        s3XLargeLink: preview,
        lowResBase64: preview,
    };
}

export async function getEntry(key, storage = defaultStorage()) {
    try {
        const entry = await storage.get(key);
        return entry || null;
    } catch (e) {
        return null;
    }
}

export async function uploadPendingImage(key, uploadFn = uploadImageFile, storage = defaultStorage()) {
    let entry = null;
    try {
        entry = await storage.get(key);
    } catch (e) {
        entry = null;
    }
    if (!entry || !entry.blob) {
        const err = new Error(`[offlineImageQueue] no stashed image for ${key}`);
        err.isMissing = true;
        throw err;
    }
    const file = entry.blob instanceof File
        ? entry.blob
        : new File([entry.blob], entry.name || key, { type: entry.type || 'image/jpeg' });
    const descriptor = await uploadFn(file);
    try { await storage.delete(key); } catch (e) { /* best-effort */ }
    return descriptor;
}

export async function discardImages(keys, storage = defaultStorage()) {
    const list = Array.isArray(keys) ? keys : [];
    for (const key of list) {
        try {
            // eslint-disable-next-line no-await-in-loop
            await storage.delete(key);
        } catch (e) { /* best-effort */ }
    }
}
